import { useEffect, useState } from 'react';
import { EntityType, RelatedEntity } from '@/types/link';
import { useLinkStore } from '@/stores/linkStore';
import { useAssetsStore } from '@/stores/assetsStore';
import { useRisks, useTasks, useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useSupplierStore } from '@/stores/supplierStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useSharedInfrastructureStore } from '@/stores/sharedInfrastructureStore';
import { supabase } from '@/integrations/supabase/client';
import type { Node } from '@xyflow/react';
import type { NodeBase } from '@/types/assets';
import { type Evidence } from '@/hooks/queries/useEvidence';
import { type Task } from '@/hooks/queries/useTasks';
import { type Risk } from '@/hooks/queries/useRisks';
import type { Supplier } from '@/types/supplier';
import type { Site } from '@/types/site';
import type { Zone } from '@/types/zone';
import type { System } from '@/types/system';
import type { SharedInfrastructure } from '@/types/sharedInfrastructure';
import type { Database } from '@/integrations/supabase/types';

type ControlSummary = Pick<Database['public']['Tables']['controls']['Row'], 'id' | 'title' | 'control_id' | 'status'>;
type IncidentSummary = Pick<Database['public']['Tables']['incidents']['Row'], 'id' | 'title' | 'priority' | 'status'>;

interface RelatedEntities {
  assets: RelatedEntity[];
  sites: RelatedEntity[];
  zones: RelatedEntity[];
  systems: RelatedEntity[];
  risks: RelatedEntity[];
  controls: RelatedEntity[];
  suppliers: RelatedEntity[];
  incidents: RelatedEntity[];
  tasks: RelatedEntity[];
  evidence: RelatedEntity[];
  shared_infrastructure: RelatedEntity[];
  loading: boolean;
}

export function useRelatedEntities(entityType: EntityType, entityId: string) {
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntities>({
    assets: [],
    sites: [],
    zones: [],
    systems: [],
    risks: [],
    controls: [],
    suppliers: [],
    incidents: [],
    tasks: [],
    evidence: [],
    shared_infrastructure: [],
    loading: true,
  });
  const { organization } = useAuth();
  const { getLinkedEntities, loadLinks } = useLinkStore();
  const { nodes } = useAssetsStore();
  const { risks } = useRisks(organization?.id);
  const { tasks } = useTasks(organization?.id);
  const { evidence } = useEvidence(organization?.id);
  const { suppliers } = useSupplierStore();
  const { sites } = useSiteStore();
  const { zones } = useZoneStore();
  const { systems } = useSystemStore();
  const { infrastructures } = useSharedInfrastructureStore();

  useEffect(() => {
    if (!organization?.id || !entityId) {
      setRelatedEntities(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchRelatedEntities = async () => {
      setRelatedEntities(prev => ({ ...prev, loading: true }));

      // Load links for this entity
      await loadLinks(entityType, entityId);

      // Get linked entity IDs
      const links = getLinkedEntities(entityType, entityId);

      const assetNodes = nodes as Node<NodeBase>[];
      const riskItems = risks as Risk[];
      const taskItems = tasks as Task[];
      const evidenceItems = evidence as Evidence[];
      const supplierItems = suppliers as Supplier[];
      const siteItems = sites as Site[];
      const zoneItems = zones as Zone[];
      const systemItems = systems as System[];
      const infrastructureItems = infrastructures as SharedInfrastructure[];

      const controlIds = links
        .filter(link => link.source_type === 'control' || link.target_type === 'control')
        .map(link => (link.source_type === 'control' ? link.source_id : link.target_id));

      const incidentIds = links
        .filter(link => link.source_type === 'incident' || link.target_type === 'incident')
        .map(link => (link.source_type === 'incident' ? link.source_id : link.target_id));

      // Fetch controls and incidents from Supabase
      let controlsData: ControlSummary[] = [];
      let incidentsData: IncidentSummary[] = [];

      if (controlIds.length) {
        const { data } = await supabase
          .from('controls')
          .select('id, title, control_id, status')
          .in('id', controlIds);

        controlsData = data ?? [];
      }

      if (incidentIds.length) {
        const { data } = await supabase
          .from('incidents')
          .select('id, title, priority, status')
          .in('id', incidentIds);

        incidentsData = data ?? [];
      }

      // Map entity IDs to actual entities
      const related: Omit<RelatedEntities, 'loading'> = {
        assets: links
          .filter(l => l.source_type === 'asset' || l.target_type === 'asset')
          .map(l => {
            const assetId = l.source_type === 'asset' ? l.source_id : l.target_id;
            const asset = assetNodes.find(node => node.id === assetId);
            if (!asset) return null;

            const entity: RelatedEntity = {
              id: asset.id,
              type: 'asset',
              name: asset.data?.label ?? 'Unnamed Asset',
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        sites: links
          .filter(l => l.source_type === 'site' || l.target_type === 'site')
          .map(l => {
            const siteId = l.source_type === 'site' ? l.source_id : l.target_id;
            const site = siteItems.find(s => s.id === siteId);
            if (!site) return null;

            const entity: RelatedEntity = {
              id: site.id,
              type: 'site',
              name: site.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        zones: links
          .filter(l => l.source_type === 'zone' || l.target_type === 'zone')
          .map(l => {
            const zoneId = l.source_type === 'zone' ? l.source_id : l.target_id;
            const zone = zoneItems.find(z => z.id === zoneId);
            if (!zone) return null;

            const entity: RelatedEntity = {
              id: zone.id,
              type: 'zone',
              name: zone.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        systems: links
          .filter(l => l.source_type === 'system' || l.target_type === 'system')
          .map(l => {
            const systemId = l.source_type === 'system' ? l.source_id : l.target_id;
            const system = systemItems.find(s => s.id === systemId);
            if (!system) return null;

            const entity: RelatedEntity = {
              id: system.id,
              type: 'system',
              name: system.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        risks: links
          .filter(l => l.source_type === 'risk' || l.target_type === 'risk')
          .map(l => {
            const riskId = l.source_type === 'risk' ? l.source_id : l.target_id;
            const risk = riskItems.find(r => r.id === riskId);
            if (!risk) return null;

            const metadata: Record<string, unknown> = { severity: risk.status };
            const entity: RelatedEntity = {
              id: risk.id,
              type: 'risk',
              name: risk.title,
              metadata,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        controls: controlsData.map(control => {
          const metadata: Record<string, unknown> = {
            control_id: control.control_id,
            status: control.status,
          };

          const entity: RelatedEntity = {
            id: control.id,
            type: 'control',
            name: control.title,
            metadata,
          };

          return entity;
        }),

        suppliers: links
          .filter(l => l.source_type === 'supplier' || l.target_type === 'supplier')
          .map(l => {
            const supplierId = l.source_type === 'supplier' ? l.source_id : l.target_id;
            const supplier = supplierItems.find(s => s.id === supplierId);
            if (!supplier) return null;

            const entity: RelatedEntity = {
              id: supplier.id,
              type: 'supplier',
              name: supplier.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        incidents: incidentsData.map(incident => {
          const metadata: Record<string, unknown> = {
            priority: incident.priority,
            status: incident.status,
          };

          const entity: RelatedEntity = {
            id: incident.id,
            type: 'incident',
            name: incident.title,
            metadata,
          };

          return entity;
        }),

        tasks: links
          .filter(l => l.source_type === 'task' || l.target_type === 'task')
          .map(l => {
            const taskId = l.source_type === 'task' ? l.source_id : l.target_id;
            const task = taskItems.find(t => t.id === taskId);
            if (!task) return null;

            const metadata: Record<string, unknown> = { status: task.status };
            const entity: RelatedEntity = {
              id: task.id,
              type: 'task',
              name: task.title,
              metadata,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        evidence: links
          .filter(l => l.source_type === 'evidence' || l.target_type === 'evidence')
          .map(l => {
            const evidenceId = l.source_type === 'evidence' ? l.source_id : l.target_id;
            const ev = evidenceItems.find(item => item.id === evidenceId);
            if (!ev) return null;

            const metadata: Record<string, unknown> = { type: ev.type };
            const entity: RelatedEntity = {
              id: ev.id,
              type: 'evidence',
              name: ev.name,
              metadata,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        shared_infrastructure: links
          .filter(l => l.source_type === 'shared_infrastructure' || l.target_type === 'shared_infrastructure')
          .map(l => {
            const infraId = l.source_type === 'shared_infrastructure' ? l.source_id : l.target_id;
            const infra = infrastructureItems.find(item => item.id === infraId);
            if (!infra) return null;

            const entity: RelatedEntity = {
              id: infra.id,
              type: 'shared_infrastructure',
              name: infra.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),
      };

      setRelatedEntities({ ...related, loading: false });
    };

    if (entityId) {
      fetchRelatedEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType, organization?.id]);

  return relatedEntities;
};
